import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import Axios from "axios";
import {
  Box,
  Typography,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActionArea,
  Tooltip,
  IconButton
} from "@mui/material";
import { DateTime } from "luxon";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { ColorModeContext, tokens } from "../theme";

function ParentForm() {
  const { pid } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const [scheduleData, setScheduleData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openRequestNewDateModal, setOpenRequestNewDateModal] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [showExpiredScreen, setShowExpiredScreen] = useState(false);
  const [showRescheduleSuccessScreen, setShowRescheduleSuccessScreen] = useState(false);
  const [status, setStatus] = useState(null);
  const [parentReject, setParentReject] = useState(false);

  useEffect(() => {
    const fetchScheduleData = async () => {
      try {
        const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/makeup/parent/${pid}`);
        if (response.status === 200) {
          if (response.data.error && response.data.error.includes("Schedule has already been selected")) {
            setStatus(4);
            setShowSuccessScreen(true);
            return;
          }
          setScheduleData(response.data);
          setStatus(response.data.status);
          setParentReject(response.data.parent_reject);
        }
      } catch (err) {
        setShowExpiredScreen(true);
      }
    };

    if (pid) {
      fetchScheduleData();
    }
  }, [pid]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleSubmit = () => {
    if (selectedOption) {
      setOpenConfirmModal(true);
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      await Axios.post(
        `${process.env.REACT_APP_URL}/api/v1/makeup/parent/${pid}`,
        {
          selected_window: selectedOption
        }
      );
      setOpenConfirmModal(false);
      setStatus(4);
      setShowSuccessScreen(true);
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Error submitting selection";
      setError(errorMessage);
      setOpenConfirmModal(false);
    }
  };

  const handleRequestNewDate = () => {
    setOpenRequestNewDateModal(true);
  };

  const handleConfirmRequestNewDate = async () => {
    try {
      await Axios.post(
        `${process.env.REACT_APP_URL}/api/v1/makeup/parent/${pid}/request-new-date`
      );
      setOpenRequestNewDateModal(false);
      setShowRescheduleSuccessScreen(true);
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Error requesting new date";
      setError(errorMessage);
      setOpenRequestNewDateModal(false);
    }
  };

  const formatSelectedOption = () => {
    if (!selectedOption) return "";
    
    const start = DateTime.fromISO(selectedOption.start, { zone: "America/New_York" });
    const end = DateTime.fromISO(selectedOption.end, { zone: "America/New_York" });
    
    return `${start.toFormat('MMMM d, yyyy')} at ${start.toFormat('HH:mm')} - ${end.toFormat('HH:mm')} EST`;
  };

  // Refactor validateOption
  const validateOption = (window) => {
    const start = DateTime.fromISO(window.start, { zone: "America/New_York" }).startOf("day");
    const now = DateTime.now().setZone("America/New_York").startOf("day");
    return start > now;
  }

  // Success Screen Component
  const SuccessScreen = () => (
    <Box sx={{
      p: 3,
      minHeight: "100vh",
      maxWidth: "1200px",
      margin: "0 auto",
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3, 
        mb: 4,
        width: '100%'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.grey[800]}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/imgs/aone-logo.png"
              alt="AONE Logo"
              style={{
                height: 40,
                width: 'auto'
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 4,
        maxWidth: 600,
        width: '100%'
      }}>
        <CheckCircleIcon
          sx={{
            fontSize: 80,
            color: colors.greenAccent[500],
            mb: 3
          }}
        />

        <Typography
          variant="h2"
          sx={{
            mb: 3,
            color: colors.primary[100],
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.5rem' },
            lineHeight: 1.2
          }}
        >
          보강 수업 일정이 확정되었습니다.
        </Typography>

        {scheduleData && (
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              color: colors.grey[300],
              fontWeight: 500,
              fontSize: { xs: '1.1rem', md: '1.3rem' }
            }}
          >
            {scheduleData.course_name} • {DateTime.fromISO(scheduleData.og_course_date).toFormat('MMMM d, yyyy')}
          </Typography>
        )}

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          flexDirection: 'column',
          maxWidth: 500
        }}>
          <Typography
            sx={{
              color: colors.grey[300],
              fontSize: '1rem',
              lineHeight: 1.5
            }}
          >
            감사합니다!
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const ExpiredScreen = () => (
    <Box sx={{
      p: 3,
      minHeight: "100vh",
      maxWidth: "1200px",
      margin: "0 auto",
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        mb: 4,
        width: '100%'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.grey[800]}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/imgs/aone-logo.png"
              alt="AONE Logo"
              style={{
                height: 40,
                width: 'auto'
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 4,
        maxWidth: 600,
        width: '100%'
      }}>
        <Box sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: colors.redAccent[900],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3
        }}>
          <CloseIcon
            sx={{
              fontSize: 32,
              color: colors.redAccent[500]
            }}
          />
        </Box>
        <Typography variant="h2" sx={{ mb: 3, color: colors.primary[100], fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.5rem' }, lineHeight: 1.2 }}>
            링크가 만료되었습니다.
        </Typography>
      </Box>
    </Box>
  );

  // Success Screen Reschedule Component
  const SuccessScreenReschedule = () => (
    <Box sx={{
      p: 3,
      minHeight: "100vh",
      maxWidth: "1200px",
      margin: "0 auto",
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        mb: 4,
        width: '100%'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.grey[800]}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/imgs/aone-logo.png"
              alt="AONE Logo"
              style={{
                height: 40,
                width: 'auto'
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 4,
        maxWidth: 600,
        width: '100%'
      }}>
        <CheckCircleIcon
          sx={{
            fontSize: 80,
            color: colors.greenAccent[500],
            mb: 3
          }}
        />

        <Typography
          variant="h2"
          sx={{
            mb: 3,
            color: colors.primary[100],
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.3rem' },
            lineHeight: 1.2
          }}
        >
          새로운 일정 요청이 접수되었습니다.
        </Typography>

        {scheduleData && (
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              color: colors.grey[300],
              fontWeight: 500,
              fontSize: { xs: '1.1rem', md: '1.3rem' }
            }}
          >
            for <strong>{scheduleData.course_name}</strong> on <strong>{DateTime.fromISO(scheduleData.og_course_date).toFormat('MMMM d, yyyy')}</strong>
          </Typography>
        )}

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          flexDirection: 'column',
          maxWidth: 500
        }}>
          <Typography
            sx={{
              color: colors.grey[300],
              fontSize: '1rem',
              lineHeight: 1.5
            }}
          >
            새로운 일정이 준비되면 이메일로 링크와 함께 안내 드리겠습니다. 감사합니다.
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // Show success screen if status is 3 or showSuccessScreen is true
  if (showSuccessScreen || status === 4) {
    return <SuccessScreen />;
  }

  // Show reschedule success screen
  if (showRescheduleSuccessScreen) {
    return <SuccessScreenReschedule />;
  }

  if (showExpiredScreen) {
    return <ExpiredScreen />;
  }

  const anyWindowDisabled = scheduleData?.selected_window?.some(window => !validateOption(window));

  return (
    <Box sx={{ p: 3, minHeight: "100vh", maxWidth: "1200px", margin: "0 auto" }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3, 
        mb: 4
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.grey[800]}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/imgs/aone-logo.png"
              alt="AONE Logo"
              style={{
                height: 40,
                width: 'auto'
              }}
            />
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <IconButton
              onClick={colorMode.toggleColorMode}
              size="small"
              sx={{
                backgroundColor: colors.primary[600],
                color: colors.primary[100],
                '&:hover': {
                  backgroundColor: colors.primary[600],
                  color: colors.primary[100],
                },
              }}
            >
              {theme.palette.mode === "dark" ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
            </IconButton>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: colors.greenAccent[500]
            }} />
            <Typography sx={{
              color: colors.grey[300],
              fontSize: '0.9rem'
            }}>
              EST Timezone
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{
          textAlign: 'center',
          py: 4
        }}>
          <Typography
            variant="h2"
            sx={{
              mb: 1,
              color: colors.primary[100],
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            보강 수업 일정 안내
          </Typography>

          {scheduleData && (
            <Typography
              variant="h4"
              sx={{
                mb: 3,
                color: colors.grey[300],
                fontWeight: 500,
                fontSize: { xs: '1.2rem', md: '1.4rem' }
              }}
            >
              <strong>{scheduleData.course_name}</strong> • <strong>{DateTime.fromISO(scheduleData.og_course_date).toFormat('MMMM d, yyyy')}</strong>
            </Typography>
          )}

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            flexDirection: 'column'
          }}>
            <Typography
              sx={{
                color: colors.grey[300],
                fontSize: '1rem'
              }}
            >
              아래 시간 중 하나를 선택해주세요.
            </Typography>

            <Box sx={{
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: '#FFF3E0'
            }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: '#ED6C02'
              }} />
              <Typography sx={{
                color: '#B76E00',
                fontSize: '0.9rem'
              }}>
              제출 후 수정이 불가능 합니다. 아래 일정은 미국 동부 시간(EST) 기준입니다.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>{error}</Typography>
      )}
      {anyWindowDisabled && (
        <Typography color="warning.main" sx={{ mb: 2, textAlign: 'center' }}>
          이미 지난 일정과 당일 일정은 선택할 수 없습니다.
        </Typography>
      )}

      {!scheduleData && !error && (
        <Typography sx={{ textAlign: 'center' }}>Loading schedule data...</Typography>
      )}

      {scheduleData && scheduleData.selected_window && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          width: '100%',
          mb: 6
        }}>
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            width: '100%',
            maxWidth: 800
          }}>
            {scheduleData.selected_window.map((window, index) => {
              const start = DateTime.fromISO(window.start, { zone: "America/New_York" });
              const end = DateTime.fromISO(window.end, { zone: "America/New_York" });
              const isSelected = selectedOption && 
                selectedOption.start === window.start && 
                selectedOption.end === window.end;
              const isDisabled = !validateOption(window);

              return (
                <Card
                  key={index}
                  sx={{
                    flex: 1,
                    minHeight: 200,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    border: isSelected ? `3px solid ${colors.greenAccent[500]}` : `1px solid ${colors.grey[700]}`,
                    transition: 'all 0.2s ease-in-out',
                    opacity: isDisabled ? 0.5 : 1,
                    pointerEvents: isDisabled ? 'none' : 'auto',
                    bgcolor: colors.primary[600],
                    '&:hover': {
                      transform: isDisabled ? 'none' : 'translateY(-2px)',
                      boxShadow: isDisabled ? 1 : 4,
                      borderColor: isSelected ? colors.greenAccent[500] : colors.greenAccent[500]
                    }
                  }}
                >
                  <CardActionArea
                    onClick={() => handleOptionSelect(window)}
                    disabled={isDisabled}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 3
                    }}
                  >
                    <CardContent sx={{
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      <Typography
                        variant="h4"
                        sx={{
                          mb: 1,
                          color: colors.grey[300],
                          fontWeight: 700
                        }}
                      >
                        {start.toFormat('MMMM d, yyyy')}
                      </Typography>

                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          color: colors.grey[300],
                          fontWeight: 500
                        }}
                      >
                        {start.toFormat('cccc')}
                      </Typography>

                      <Typography
                        variant="h5"
                        sx={{
                          color: colors.primary[100],
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        {start.toFormat('HH:mm')} - {end.toFormat('HH:mm')}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.grey[300],
                          fontSize: '0.9rem'
                        }}
                      >
                        EST Timezone
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      <Box sx={{ 
        display: "flex", 
        flexDirection: "column",
        alignItems: "center", 
        gap: 3,
        pb: 12 
      }}>
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          width: "100%",
          maxWidth: 400
        }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedOption}
            sx={{
              width: "100%",
              minHeight: 56,
              fontSize: '1.1rem',
              fontWeight: 600,
              bgcolor: colors.greenAccent[500],
              color: colors.primary[500],
              borderRadius: 2,
              boxShadow: 3,
              textTransform: "none",
              '&:hover': {
                bgcolor: colors.greenAccent[600],
                boxShadow: 4,
                transform: "translateY(-1px)",
                transition: "all 0.2s ease-in-out"
              },
              '&:disabled': {
                bgcolor: colors.grey[300],
                color: colors.grey[100],
                transform: "none",
                boxShadow: 1
              }
            }}
          >
            제출하기
          </Button>
          
          <Tooltip 
            title={parentReject ? "새로운 일정 요청은 한 번만 가능합니다." : ""}
            arrow
          >
            <span style={{ width: "100%" }}> 
              <Button
                variant="outlined"
                disabled={parentReject || status === null}
                onClick={handleRequestNewDate}
                sx={{
                  width: "100%",
                  minHeight: 56,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderColor: colors.primary[100],
                  color: colors.primary[100],
                  borderRadius: 2,
                  textTransform: "none",
                  '&:hover': {
                    transform: "translateY(-1px)",
                    transition: "all 0.2s ease-in-out"
                  },
                  '&:disabled': {
                    borderColor: colors.grey[500],
                    color: colors.grey[300]
                  }
                }}
              >
                새로운 일정 요청하기
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Dialog
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.primary[600] } }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pt: 3,
          pb: 2,
          color: colors.primary[100],
          fontWeight: 600
        }}>
          제출하기
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 3 }}>
          <Typography sx={{ mb: 2, color: colors.grey[300] }}>
            아래 일정을 선택하셨습니다:
          </Typography>
          <Typography
            sx={{
              pl: 2,
              mb: 1,
              fontWeight: 600,
              color: colors.primary[100]
            }}
          >
            {formatSelectedOption()}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => setOpenConfirmModal(false)}
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 500,
              borderColor: colors.primary[100],
              color: colors.primary[100],
              borderRadius: 2,
              textTransform: "none"
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 600,
              bgcolor: colors.greenAccent[500],
              color: colors.primary[500],
              borderRadius: 2,
              textTransform: "none",
              '&:hover': {
                bgcolor: colors.greenAccent[500]
              }
            }}
          >
            제출
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRequestNewDateModal}
        onClose={() => setOpenRequestNewDateModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.primary[600] } }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pt: 3,
          pb: 2,
          color: colors.primary[100],
          fontWeight: 600
        }}>
          새로운 일정 요청하기
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 3 }}>
          <Typography sx={{ mb: 2, color: colors.grey[300] }}>
            새로운 일정을 요청하시겠습니까?
          </Typography>
          <Typography sx={{ mb: 2, color: colors.grey[300] }}>
            현재 보강 수업 일정이 불가능한 경우, 새로운 일정을 요청할 수 있습니다.
          </Typography>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 1,
            bgcolor: '#FFF3E0',
            mb: 2
          }}>
            <Box sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#ED6C02'
            }} />
            <Typography sx={{
              color: '#B76E00',
              fontSize: '0.9rem'
            }}>
              주의: 새로운 일정 요청은 한 번만 가능합니다.
            </Typography>
          </Box>
          <Typography sx={{ color: colors.grey[300] }}>
            제출 후 새로운 일정 옵션을 고를 수 있는 링크를 받을 수 있습니다.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => setOpenRequestNewDateModal(false)}
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 500,
              borderColor: colors.primary[100],
              color: colors.primary[100],
              borderRadius: 2,
              textTransform: "none"
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleConfirmRequestNewDate}
            variant="contained"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 600,
              bgcolor: colors.greenAccent[500],
              color: colors.primary[500],
              borderRadius: 2,
              textTransform: "none",
              '&:hover': {
                bgcolor: colors.greenAccent[500]
              }
            }}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ParentForm;